/**
 * Planning v2 (docs/ux-v2/SPEC.md § 4.1 : PL1 à PL13 ; retours client P1 à P8). Routes : index, studio/:id,
 * studio/:id/week/:weekNumber, coach/:id, cours/:id, studio/:id/cours/:type.
 * Hors page de réservation : mode coach (`[mindbody coach="…"]`, fiches coach) et mode cours (`[mindbody cours="…"]`,
 * pages de cours, retours de Daphné du 19/10 : « une preview du planning sur ces cours dans chaque page, comme ce
 * qu'on a fait pour les pages coachs ») : planning du site seul, coach ou type figé, h2, cours ouverts sur la page
 * de réservation.
 *
 * Données : une requête `class/planning` par semaine et par marque affichée (retours du 07/10 : à l'ouverture, la
 * marque du site visité ; une autre marque de la ville est chargée au clic sur son étiquette, avec un squelette), plus
 * les listes déjà lues par la v1 (site/locations, staff/coaches, client/visits si connecté) : autant d'appels au
 * chargement que la v1, aucun au changement de filtre Studios / Coachs / Cours. Les cartes s'affichent dès l'arrivée
 * du planning ; les étiquettes d'état attendent les cours réservés du client (client/visits) sans retarder le reste.
 * Logique métier reprise de la v1 par import (fenêtre de réservation, types de cours, mode coach, Run Club, studio
 * masqué) : rien n'est réécrit, seulement ré-habillé.
 */
import { useMediaQuery } from '@chakra-ui/react';
import { addDays, addWeeks, isSameDay } from 'date-fns';
import { useListCoaches, useListLocations, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCourseTypeOptions, matchesCourseType } from 'screens/Classes/courseTypeFilters';
import type { MindbodyClass } from 'types/class';
import { isClassBookable } from 'utils/functions';
import { trackViewPlanning } from 'utils/tracking';
import { resolveUserError } from 'utils/userErrorMessages';
import { usePlanning, type PlanningClass } from '../../api/planning';
import { resolveBrands, shortStudioName, type PlanningBrand } from '../../lib/planning/brands';
import {
  coachGroups as buildCoachGroups,
  coachesWithFilters,
  courseTypeOptions,
  defaultBrandOf,
  defaultFilters,
  filterClasses,
  hasActiveFilters,
  namesFromClasses,
  prefilterFromPath,
  selectBrand,
  siteKey,
  siteOfKey,
  studioGroups as buildStudioGroups,
  studiosWithClasses,
  studiosWithTypes,
  toggleKey,
  type PlanningFilters,
} from '../../lib/planning/filters';
import { joinNames, planningSubtitle } from '../../lib/planning/heading';
import { releaseServerSkeleton, serverSkeletonPending } from '../../lib/planning/skeleton';
import { eventStudioName, runClubStudioRouteId } from '../../lib/planning/eventStudios';
import { classStatus, classTarget, RUN_CLUB_HOME_SITE_ID } from '../../lib/planning/status';
import {
  canGoToPreviousWeek,
  effectiveSelectedDay,
  isoWeekOf,
  shiftSelectedDay,
  weekDays,
  weekRangeLabel,
  weekStartFromNumber,
  weekStartOf,
} from '../../lib/planning/week';
import { cssThemeTokenReader, readSettings, resolveTokens } from '../../theme/tokens';
import { typo } from '../../lib/typo';
import { deElide } from '../Class/lib/format';
import { ClassCard } from './ClassCard';
import { readPlanningConfig, splitPlanningTitle } from './config';
import { DayView } from './DayView';
import { Filters } from './Filters';
import { IconInfo } from './icons';
import { usePlanningStore } from './store';
import { WeekBar } from './WeekBar';
import { WeekGrid } from './WeekGrid';
import { injectV2Css } from '../../styles/inject';
import planningCss from './planning.css?inline';

injectV2Css('screens/Planning/planning.css', planningCss);

/**
 * Largeur du planning à partir de laquelle la grille 7 jours remplace la vue jour : 7 cartes d'au moins 163 px
 * (« LISTE D’ATTENTE ↗ », 135 px, + marges intérieures et liserés de la carte) et 6 gouttières de 10 px. Mesurée sur le
 * planning lui-même, pas sur la fenêtre : la colonne de contenu dépend du thème du site. Revue du 04/10 : avec la
 * grille dès 920 px de fenêtre, les cartes faisaient 115 à 130 px et l'étiquette d'état sortait de la carte par la
 * gauche (1024), la marque était tronquée (920). En dessous : vue jour (DayView), limitée à une colonne de lecture
 * au-delà de 640 px (planning.css).
 */
const GRID_MIN_WIDTH = 7 * 163 + 6 * 10;

/** Largeur du bloc (ResizeObserver), mesurée avant le premier affichage ; null tant qu'elle est inconnue. */
function useElementWidth(ref: RefObject<HTMLElement>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** Heure courante, rafraîchie chaque minute (états « Terminé », fenêtre de réservation). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function PlanningV2() {
  const config = useMemo(readPlanningConfig, []);
  const { currentSiteId } = config;
  const coachMode = config.coachId !== null;
  const lockedCoachKey = config.coachId !== null ? siteKey(currentSiteId, config.coachId) : null;
  // Mode cours : le coach du shortcode, s'il y en a un, l'emporte.
  const courseMode = !coachMode && config.courseType !== null;
  const lockedTypeKey = courseMode && config.courseType !== null ? siteKey(currentSiteId, config.courseType) : null;
  /** App hors de la page de réservation (fiche coach, page de cours) : site seul, cours ouverts sur la réservation. */
  const embedded = coachMode || courseMode;
  const now = useNow();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLoggedIn } = useUserContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const planningWidth = useElementWidth(rootRef);
  // Avant la première mesure : estimation par la fenêtre (colonne de contenu du thème monday-2026 à 1280 px).
  const [wideViewport] = useMediaQuery('(min-width: 1280px)', { ssr: false });
  const isDesktop = planningWidth !== null ? planningWidth >= GRID_MIN_WIDTH : wideViewport;

  // Couleur du site et fonds des cartes (même calcul que mountV2) : couleurs de marque lisibles (AA).
  const tokens = useMemo(() => resolveTokens(readSettings(), cssThemeTokenReader()), []);

  /* ------------------------------------------------------------- état, pré-filtres des routes */

  const store = usePlanningStore();
  // = Filters/CourseTypeFilter.tsx : `cours/:type` n'est pris que si le filtre est actif et le type connu du site.
  const ownTypeValues = useMemo(
    () => (config.courseTypeFilterEnabled ? getCourseTypeOptions(currentSiteId).map((o) => o.value) : []),
    [config.courseTypeFilterEnabled, currentSiteId],
  );
  const prefilter = useMemo(
    () => prefilterFromPath(pathname, currentSiteId, ownTypeValues, config.eventSplit),
    [pathname, currentSiteId, ownTypeValues, config.eventSplit],
  );
  // Une route à pré-filtres pas encore appliquée prime dès le premier rendu (aucune requête pour une autre semaine).
  const pending = prefilter && store.appliedPath !== pathname ? prefilter : null;
  const pendingWeekStart = useMemo(
    () => (pending?.week ? weekStartFromNumber(pending.week, new Date()) : null),
    [pending],
  );
  // Filtres de la route (pré-filtre pas encore appliqué) ou choisis ; null = filtres par défaut (plus bas, PL7).
  const chosenFilters = pending ? pending.filters : store.filters;
  const weekStart = pendingWeekStart ?? store.weekStart;

  useEffect(() => {
    if (pending) {
      usePlanningStore.setState({
        filters: pending.filters,
        weekStart: pendingWeekStart ?? usePlanningStore.getState().weekStart,
        selectedDay: null,
        appliedPath: pathname,
      });
    } else if (!prefilter && usePlanningStore.getState().appliedPath !== pathname) {
      usePlanningStore.setState({ appliedPath: pathname });
    }
  }, [pending, pendingWeekStart, prefilter, pathname]);

  /** `null` : retour aux filtres par défaut (« Effacer »). */
  const setFilters = useCallback(
    (next: PlanningFilters | null) => {
      usePlanningStore.setState({ filters: next });
      // = Filters/Studios.tsx, Coaches.tsx : changer la sélection quitte l'URL pré-filtrée.
      if (prefilter) navigate('/', { replace: true });
    },
    [navigate, prefilter],
  );

  /* -------------------------------------------------------------------------------- données */

  const { week, year } = isoWeekOf(weekStart);
  /*
   * Marque chargée (retours du 07/10) : la marque choisie dans le filtre, sinon celle du site visité. Les modes coach
   * et cours et les filtres par défaut (marque du site) chargent le site courant ; le serveur refuse un site hors de la
   * ville.
   */
  const loadSiteId = embedded ? currentSiteId : (chosenFilters?.brand ?? currentSiteId);
  const planning = usePlanning({ week, year, coachId: config.coachId, siteId: loadSiteId, currentSiteId });
  const { data: locations } = useListLocations();
  const { data: coaches, isLoading: isCoachesLoading } = useListCoaches();
  const { data: visits, isLoading: isVisitsLoading } = useListVisits();
  const data = planning.data;

  // Tunnel de réservation : planning affiché (une fois par site et semaine chargés).
  useEffect(() => {
    if (data?.classes) trackViewPlanning({ key: `${loadSiteId}:${year}-W${week}`, count: data.classes.length });
  }, [data, loadSiteId, week, year]);

  const multiBrand = !embedded && (data && !data.meta.fallback ? data.meta.multiBrand : config.multiBrand);

  const siteIds = useMemo(() => {
    if (!multiBrand) return [currentSiteId];
    const fromData = (data?.meta.siteIds ?? []).map(String);
    if (fromData.length > 1) return fromData;
    const fromConfig = config.rawBrands.map((brand) => String(brand.siteId ?? '')).filter(Boolean);
    return fromConfig.length > 1 ? fromConfig : [currentSiteId];
  }, [multiBrand, data, config.rawBrands, currentSiteId]);
  const showBrands = multiBrand && siteIds.length > 1;

  /*
   * Décision de Luc du 04/10 (PLAN P4, SPEC PL7) : en vue multi-marques, le planning s'ouvre sur la marque du site
   * visité ; les autres marques sont à un clic. Retours du 07/10 (Daphné) : plus de « Toutes » (rare, et elle
   * alourdissait la lecture et le chargement) : une marque est toujours choisie ; une route à pré-filtre (Id du site
   * courant) s'ouvre sur la marque du site. Sans filtre Marque (planning mono-site, mode coach), aucune marque n'est
   * retenue. Mode coach : le coach du shortcode est toujours appliqué (= Filters/Coaches.tsx) ; mode cours : le type
   * du shortcode, de même.
   */
  const defaultBrand = defaultBrandOf(showBrands, siteIds, currentSiteId);
  const initialFilters = useMemo(() => defaultFilters(defaultBrand), [defaultBrand]);
  const filters: PlanningFilters = useMemo(() => {
    const chosen = chosenFilters ?? initialFilters;
    if (lockedCoachKey) return { ...chosen, brand: null, coaches: [lockedCoachKey] };
    if (lockedTypeKey) return { ...chosen, brand: null, types: [lockedTypeKey] };
    if (!showBrands) return chosen.brand === null ? chosen : { ...chosen, brand: null };
    return chosen.brand !== null && siteIds.includes(chosen.brand) ? chosen : { ...chosen, brand: defaultBrand };
  }, [chosenFilters, initialFilters, showBrands, lockedCoachKey, lockedTypeKey, siteIds, defaultBrand]);

  const locationNamesBySite = useMemo(() => {
    const bySite: Record<string, string[]> = {};
    (Array.isArray(locations) ? locations : []).forEach((location) => {
      const site = String(location.SiteID);
      (bySite[site] = bySite[site] ?? []).push(location.Name);
    });
    return bySite;
  }, [locations]);

  const brands = useMemo(
    () =>
      resolveBrands({
        siteIds,
        raw: config.rawBrands,
        currentSiteId,
        currentName: config.brandName,
        locationNamesBySite,
        palette: { currentColor: tokens.brand, ink: tokens.ink, backgrounds: [tokens.paper, tokens.green50] },
      }),
    [siteIds, config.rawBrands, config.brandName, currentSiteId, locationNamesBySite, tokens],
  );
  const brandBySite = useMemo(() => new Map(brands.map((brand) => [brand.siteId, brand])), [brands]);
  const brandOf = useCallback(
    (siteId: string): PlanningBrand =>
      brandBySite.get(siteId) ?? {
        siteId,
        name: '',
        color: tokens.ink,
        textColor: tokens.ink,
        url: null,
        tagline: '',
        isCurrent: siteId === currentSiteId,
      },
    [brandBySite, tokens.ink, currentSiteId],
  );
  const colorOf = useCallback((siteId: string) => brandOf(siteId).color, [brandOf]);
  const labelOf = useCallback((siteId: string) => brandOf(siteId).name, [brandOf]);

  const brandNames = useMemo(
    () => [...brands.map((brand) => brand.name), config.brandName].filter(Boolean),
    [brands, config.brandName],
  );
  const shortName = useCallback((name: string) => shortStudioName(name, brandNames), [brandNames]);

  // Visites du client (cours réservés), toutes marques : clé « site:classId ».
  const mine = useMemo(() => {
    const keys = new Set<string>();
    (Array.isArray(visits) ? visits : []).forEach((visit) => {
      if (visit.LateCancelled) return;
      keys.add(siteKey(visit.SiteId ? String(visit.SiteId) : currentSiteId, visit.ClassId));
    });
    return keys;
  }, [visits, currentSiteId]);

  /* ------------------------------------------------------------------------------- filtrage */

  const filterContext = useMemo(
    () => ({
      siteIds,
      matchesType: (name: string, value: string, site: string) => matchesCourseType(name, [value], site),
      lockedCoachKey,
      eventSplit: config.eventSplit,
    }),
    [siteIds, lockedCoachKey, config.eventSplit],
  );
  const visible = useMemo(() => filterClasses(data?.classes ?? [], filters, filterContext), [data, filters, filterContext]);

  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const classesByDay = useMemo(() => {
    const byDay: PlanningClass[][] = days.map(() => []);
    visible.forEach((c) => {
      const start = new Date(c.StartDateTime);
      const index = days.findIndex((day) => isSameDay(day, start));
      if (index >= 0) byDay[index].push(c);
    });
    return byDay;
  }, [visible, days]);

  // Type de cours choisi : seulement les studios qui en ont un cours dans la semaine chargée (recette RIISE du 28/10) ;
  // tant que la semaine n'est pas chargée, la liste reste complète.
  const typedStudios = useMemo(
    () => (data ? studiosWithTypes(data.classes, filters, filterContext) : null),
    [data, filters, filterContext],
  );
  // Studio Monday Event séparé (réglage du site) : son entrée d'origine n'est proposée que si elle a des cours chargés.
  const presentStudios = useMemo(
    () => (data ? studiosWithClasses(data.classes, config.eventSplit) : null),
    [data, config.eventSplit],
  );
  const studioOptions = useMemo(
    () =>
      buildStudioGroups(locations, siteIds, filters.brand, labelOf, shortName, typedStudios, filters.studios, {
        split: config.eventSplit,
        present: presentStudios,
      }),
    [locations, siteIds, filters.brand, labelOf, shortName, typedStudios, filters.studios, config.eventSplit, presentStudios],
  );
  // Type de cours ou studio choisi : seulement les coachs qui y donnent un cours dans la semaine chargée (Daphné, 30/10).
  const filteredCoaches = useMemo(
    () => (data ? coachesWithFilters(data.classes, filters, filterContext) : null),
    [data, filters, filterContext],
  );
  const coachOptions = useMemo(
    () => buildCoachGroups(coaches, siteIds, filters.brand, labelOf, filteredCoaches, filters.coaches),
    [coaches, siteIds, filters.brand, labelOf, filteredCoaches, filters.coaches],
  );
  // Noms connus par les cours chargés : route à pré-filtre (#/studio/4, #/coach/…) avant l'arrivée des listes.
  const knownNames = useMemo(
    () => namesFromClasses(data?.classes, shortName, config.eventSplit),
    [data, shortName, config.eventSplit],
  );
  // Mode cours : pas de rangée « Cours » (le type est celui de la page).
  const typeOptions = useMemo(
    () =>
      config.courseTypeFilterEnabled && !courseMode
        ? courseTypeOptions(siteIds, filters.brand, getCourseTypeOptions)
        : [],
    [config.courseTypeFilterEnabled, courseMode, siteIds, filters.brand],
  );

  const canClear = hasActiveFilters(filters, { lockedCoachKey, lockedTypeKey, defaultBrand });

  /**
   * Focus gardé après une action qui fait disparaître son bouton (revue du 05/10 : focus perdu sur BODY après
   * « Effacer », le Tab suivant sautait les filtres). Après la mise à jour du rendu.
   */
  const focusLater = useCallback((selector: string) => {
    window.setTimeout(() => rootRef.current?.querySelector<HTMLElement>(selector)?.focus(), 0);
  }, []);
  const clearFilters = useCallback(
    (pointer = false) => {
      setFilters(null);
      /*
       * L'étiquette de la marque du site (vue multi-marques), sinon la liste Studios. Un <input> correspond toujours à
       * :focus-visible : après un clic, la liste est marquée « pointeur » (planning.css), sans l'anneau de focus du
       * clavier (revue du 05/10 : anneau rouge autour de « Tous les studios » après un clic sur « Effacer » en mode
       * coach, lu comme une erreur).
       */
      if (pointer) {
        const select = rootRef.current?.querySelector<HTMLElement>('.mmv2-filter-select[data-filter="studios"]');
        if (select && !rootRef.current?.querySelector('.mmv2-filters .mmv2-chip[aria-pressed]')) select.dataset.pointer = '';
      }
      focusLater('.mmv2-filters .mmv2-chip[aria-pressed="true"], #mmv2-studios');
    },
    [setFilters, focusLater],
  );

  /* --------------------------------------------------------------------- semaine, jour choisi */

  const selectedDay = effectiveSelectedDay(store.selectedDay, weekStart, now);

  const goToWeek = useCallback(
    (delta: number) => {
      const state = usePlanningStore.getState();
      const from = effectiveSelectedDay(state.selectedDay, weekStart, new Date());
      usePlanningStore.setState({
        weekStart: weekStartOf(addWeeks(weekStart, delta)),
        selectedDay: shiftSelectedDay(from, delta, new Date()),
      });
    },
    [weekStart],
  );

  const selectDay = useCallback((day: Date) => usePlanningStore.setState({ selectedDay: day }), []);

  const nextDay = useCallback(() => {
    const next = addDays(selectedDay, 1);
    usePlanningStore.setState(
      next.getTime() >= addDays(weekStart, 7).getTime()
        ? { weekStart: weekStartOf(next), selectedDay: next }
        : { selectedDay: next },
    );
  }, [selectedDay, weekStart]);

  /* ------------------------------------------------------------------------------- rendu */

  // Squelette tant que le planning de la marque affichée n'est pas arrivé ; les cours réservés du client (client/visits)
  // ne le retardent plus : en attendant, les étiquettes d'état des cartes sont réservées, vides (statusPending).
  const isLoading = planning.isLoading;
  /*
   * Squelette rendu par le serveur affiché jusqu'au montage (lib/planning/skeleton.ts, SPEC § 12) : son message
   * « Chargement du planning… » a déjà été donné, celui de React reste vide pendant ce premier chargement ; les cartes
   * qui le remplacent apparaissent en fondu, même si elles sont déjà en cache au montage.
   */
  const [fromServerSkeleton, setFromServerSkeleton] = useState(serverSkeletonPending);
  useEffect(() => {
    if (!fromServerSkeleton || isLoading) return;
    releaseServerSkeleton();
    setFromServerSkeleton(false);
  }, [fromServerSkeleton, isLoading]);
  // Planning quitté avant la fin de ce premier chargement (fiche cours…) : au retour, chargement ordinaire, avec son message.
  useEffect(() => releaseServerSkeleton, []);
  const statusPending = Boolean(isLoggedIn && isVisitsLoading);
  const headingLevel = embedded ? 2 : 1;
  const sectionHeadingLevel = headingLevel === 1 ? 2 : 3;
  const runClubBrand = brandBySite.get(RUN_CLUB_HOME_SITE_ID);
  const runClubUrl = runClubBrand?.url ?? null;

  // Cours déjà commencé (« En cours » ou « Terminé » de classStatus), sauf le cours en cours réservé par le client.
  const isStarted = useCallback(
    (c: PlanningClass) => {
      const start = new Date(c.StartDateTime).getTime();
      if (start > now.getTime()) return false;
      const end = c.EndDateTime ? new Date(c.EndDateTime).getTime() : NaN;
      const ended = (Number.isFinite(end) && end > start ? end : start) <= now.getTime();
      return ended || !mine.has(siteKey(String(c.SiteId), c.Id));
    },
    [now, mine],
  );

  const renderCard = (c: PlanningClass, row = false) => {
    const site = String(c.SiteId);
    const brand = brandOf(site);
    const status = classStatus(c, {
      now,
      isCurrentSite: site === currentSiteId,
      // = utils/functions.ts : mêmes champs lus (StartDateTime, IsCourse) que sur un MindbodyClass complet.
      isBookable: (x) => isClassBookable(x as unknown as MindbodyClass),
      isMine: mine.has(siteKey(site, c.Id)),
      brand,
      numberOfDays: config.numberOfDays,
    });
    const target = classTarget(c, {
      status,
      currentSiteId,
      coachMode: embedded,
      reservationUrl: config.reservationUrl,
      brand,
      runClubUrl,
      runClubName: runClubBrand?.name,
      runClubStudioId: runClubStudioRouteId(config.eventSplit),
    });
    return (
      <ClassCard
        mindbodyClass={c}
        status={status}
        target={target}
        brand={brand}
        showBrandTag={showBrands}
        studioLabel={eventStudioName(c, config.eventSplit) ?? shortName(c.Location?.Name ?? '')}
        row={row}
        statusPending={statusPending}
      />
    );
  };

  const coachName =
    coaches?.find((coach) => siteKey(String(coach.SiteId), coach.Id) === lockedCoachKey)?.FirstName ??
    data?.classes.find((c) => siteKey(String(c.SiteId), c.Staff?.Id) === lockedCoachKey)?.Staff.FirstName;
  // Mode coach (fiche coach du thème : son h1 est le prénom, sa section titre déjà « Planning ») : un h2 qui nomme
  // le coach, sans sous-titre. Tant que le prénom peut encore arriver (listes en cours de chargement), le titre est
  // réservé (même hauteur, vide) : il ne passe plus de « Ses cours » à « Les cours de Cynthia » sous les yeux.
  const coachTitlePending = coachMode && !coachName && (isCoachesLoading || planning.isLoading);
  // Mode cours (page de cours du thème, son h1 nomme déjà le cours) : « Les cours de reformer ».
  const courseLabel = courseMode
    ? (getCourseTypeOptions(currentSiteId).find((o) => o.value === config.courseType)?.label ?? '')
    : '';
  const title = coachMode
    ? coachName
      ? `Les cours ${deElide(coachName)}`
      : 'Ses cours'
    : courseLabel
      ? `Les cours ${deElide(courseLabel.toLowerCase())}`
      : config.planningTitle;
  // Titre du réglage seulement : « Planning : réserver un cours RIISE » = titre + sous-titre gris dans le même h1.
  const titleParts = coachMode || courseLabel ? { main: title, sub: '' } : splitPlanningTitle(title);
  const subtitle = coachMode
    ? ''
    : planningSubtitle({
        filters,
        showBrands,
        brands,
        studioOptions: [
          ...studioOptions.flatMap((group) => group.options),
          ...[...knownNames.studios].map(([value, label]) => ({ value, label, siteId: siteOfKey(value) })),
        ],
        coachOptions: [
          ...coachOptions.flatMap((group) => group.options),
          ...[...knownNames.coaches].map(([value, label]) => ({ value, label, siteId: siteOfKey(value) })),
        ],
        lockedCoachKey,
      });

  const Title = headingLevel === 1 ? 'h1' : 'h2';
  // Message de la v1 (locales) en typographie v2 : « n’a pas pu s’afficher » (apostrophes typographiques).
  const loadError = (({ title, description }) => ({ title: typo(title), description: typo(description) }))(
    resolveUserError('loadClasses'),
  );
  const failedBrands = (data?.meta.failedSiteIds ?? []).map((id) => labelOf(String(id))).filter(Boolean);
  const weekIsEmpty = !isLoading && !planning.isError && visible.length === 0;
  // Échec du chargement : l'encadré seul, ni grille ni vue jour vides (« 0 cours », « Aucun cours », revue du 05/10).
  const loadFailed = planning.isError && !isLoading && !data;
  const hasStarred = visible.some((c) => (c.ClassDescription?.Name ?? '').includes('*'));
  // Semaine vide sur la marque affichée, sans autre filtre (PL7, PL12) : la nommer. Les autres marques ne sont pas
  // chargées (retours du 07/10) : leurs étiquettes, juste au-dessus, restent à un clic.
  const emptyBrand =
    weekIsEmpty &&
    filters.brand !== null &&
    !(filters.studios.length || filters.coaches.length || filters.types.length || filters.slots.length)
      ? labelOf(filters.brand)
      : '';

  const nextWeekButton = (
    <button
      type="button"
      className="mmv2-linkish"
      onClick={() => {
        goToWeek(1);
        // Le bouton disparaît si la semaine suivante a des cours : focus sur « Semaine suivante », qui reste en place
        // (la plage de semaine, un <b>, ne prend pas le focus : il tombait sur BODY, revue du 05/10).
        focusLater('.mmv2-week__nav button[aria-label="Semaine suivante"]');
      }}
    >
      Voir la semaine suivante
    </button>
  );
  const clearButton = (
    <button type="button" className="mmv2-linkish" onClick={(event) => clearFilters(event.detail > 0)}>
      Effacer les filtres
    </button>
  );

  return (
    <div
      ref={rootRef}
      className="mmv2-planning"
      data-multibrand={showBrands || undefined}
      data-coach={coachMode || undefined}
      data-view={isDesktop ? 'grid' : 'day'}
      data-week={`${year}-${String(week).padStart(2, '0')}`}
    >
      <header className="mmv2-planning__head">
        {coachTitlePending ? (
          <Title className="mmv2-planning__title mmv2-planning__title--coach" aria-hidden="true">
            {'\u00a0'}
          </Title>
        ) : (
          <Title className={`mmv2-planning__title${embedded ? ' mmv2-planning__title--coach' : ''}`}>
            {titleParts.sub ? (
              <>
                {titleParts.main}
                <span className="mmv2-planning__title-sub"> {titleParts.sub}</span>
              </>
            ) : (
              title
            )}
          </Title>
        )}
        {subtitle === null ? (
          // Nom du studio ou du coach choisi encore inconnu : la ligne est réservée, sans texte provisoire.
          <p className="mmv2-planning__subtitle" aria-hidden="true">
            {'\u00a0'}
          </p>
        ) : subtitle ? (
          <p className="mmv2-planning__subtitle">{subtitle}</p>
        ) : null}
      </header>

      <Filters
        filters={filters}
        multiBrand={showBrands}
        brands={brands}
        colorOf={colorOf}
        studioGroups={studioOptions}
        coachGroups={coachOptions}
        knownNames={knownNames}
        courseTypes={typeOptions}
        groupText={config.groupText}
        coachLocked={coachMode}
        canClear={canClear}
        onBrand={(siteId) => setFilters(selectBrand(filters, siteId))}
        onStudios={(studios) => setFilters({ ...filters, studios })}
        onCoaches={(list) => setFilters({ ...filters, coaches: list })}
        onToggleType={(key) => setFilters({ ...filters, types: toggleKey(filters.types, key) })}
        onToggleSlot={(slot) => setFilters({ ...filters, slots: toggleKey(filters.slots, slot) })}
        onClear={clearFilters}
      />

      <WeekBar
        rangeLabel={weekRangeLabel(weekStart, now)}
        canGoBack={canGoToPreviousWeek(weekStart, now)}
        onPrevious={() => goToWeek(-1)}
        onNext={() => goToWeek(1)}
      />

      <p className="mmv2-sr-only" role="status">
        {isLoading
          ? fromServerSkeleton
            ? ''
            : 'Chargement du planning…'
          : loadFailed
            ? 'Le planning n’a pas pu être chargé.'
            : `${visible.length} cours cette semaine`}
      </p>

      {planning.isError ? (
        <div className="mmv2-notice mmv2-notice--warm mmv2-planning__notice">
          <IconInfo />
          <div>
            <b>{loadError.title}.</b> {loadError.description}{' '}
            <button type="button" className="mmv2-linkish" onClick={() => planning.refetch()}>
              Réessayer
            </button>
          </div>
        </div>
      ) : null}

      {failedBrands.length > 0 ? (
        <div className="mmv2-notice mmv2-notice--warm mmv2-planning__notice">
          <IconInfo />
          <div>
            Les cours de {joinNames(failedBrands)} n’ont pas pu être chargés.{' '}
            <button type="button" className="mmv2-linkish" onClick={() => planning.refetch()}>
              Réessayer
            </button>
          </div>
        </div>
      ) : null}

      {weekIsEmpty ? (
        <div className="mmv2-notice mmv2-planning__notice mmv2-planning__empty" role="status">
          <IconInfo />
          <div>
            {emptyBrand
              ? `Aucun cours ${emptyBrand} cette semaine.`
              : canClear
                ? 'Aucun cours ne correspond à ces filtres cette semaine.'
                : 'Aucun cours cette semaine.'}
            <span className="mmv2-planning__empty-actions">
              {canClear && !emptyBrand ? clearButton : null}
              {nextWeekButton}
            </span>
          </div>
        </div>
      ) : null}

      {weekIsEmpty || loadFailed ? null : (
        /*
         * Lien d'évitement (WCAG 2.4.1) : une carte = un arrêt de tabulation, jusqu'à 700 par semaine. Bouton et non
         * lien « #… » : l'app route par le fragment de l'adresse.
         */
        <button
          type="button"
          className="mmv2-skip"
          onClick={() => rootRef.current?.querySelector<HTMLElement>('.mmv2-planning__end')?.focus()}
        >
          Passer le planning
        </button>
      )}

      {/*
       * Semaine sans cours : l'encadré seul (maquette 05-planning-vide-390). En vue jour, le sélecteur, un second
       * « Aucun cours. » et « Jour suivant » faisaient parcourir 7 jours vides (revue du 05/10).
       */}
      {loadFailed || (weekIsEmpty && !isDesktop) ? null : isDesktop ? (
        <WeekGrid
          days={days}
          today={now}
          classesByDay={classesByDay}
          headingLevel={sectionHeadingLevel}
          renderCard={(c) => renderCard(c)}
          isLoading={isLoading}
          skeletonBrandTag={showBrands}
          revealOnMount={fromServerSkeleton}
        />
      ) : (
        <DayView
          days={days}
          today={now}
          selectedDay={selectedDay}
          classesByDay={classesByDay}
          headingLevel={sectionHeadingLevel}
          renderCard={(c) => renderCard(c, true)}
          isLoading={isLoading}
          onSelectDay={selectDay}
          onNextDay={nextDay}
          isStarted={isStarted}
          emptyDay={<p className="mmv2-dayview__empty">Aucun cours ce jour-là.</p>}
          skeletonBrandTag={showBrands}
          revealOnMount={fromServerSkeleton}
        />
      )}

      <span className="mmv2-planning__end mmv2-sr-only" tabIndex={-1}>
        Fin du planning
      </span>

      {hasStarred ? <p className="mmv2-planning__note">* Cours accessibles avec les packs étudiants.</p> : null}
    </div>
  );
}
